import React from 'react';
import { Beaker, Wrench, TestTube, Factory } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Labs = () => {
  const labs = [
    {
      institution: 'IIT Guwahati',
      period: '2017-2023',
      description: 'Established a full-fledged composite manufacturing and testing laboratory',
      facilities: [
        'Vacuum assisted resin transfer moulding setup',
        'Resin dispenser',
        'NDT Ultrasonic scanner',
        'Mixed-mode bending (MMB), Double cantilever beam (DCB) and End-notch flexure (ENF) setup',
        'Shimadzu static UTM and axial-torsion dynamic UTM',
      ],
      color: 'from-blue-600 to-cyan-500',
      icon: <Factory size={48} />,
    },
    {
      institution: 'IIT Palakkad',
      period: '2023-Present',
      description: 'Setting up state-of-the-art composite testing laboratory',
      facilities: [
        'Advanced composite testing equipment',
        'Material characterization facilities',
        'Research-grade testing apparatus',
      ],
      color: 'from-purple-600 to-pink-500',
      icon: <TestTube size={48} />,
    },
  ];

  return (
    <section id="labs" className="section-container bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-200 to-purple-200 rounded-full filter blur-3xl opacity-20 animate-float"></div>
      
      <h2 className="section-title">Laboratory Establishment</h2>
      
      <div className="w-full relative z-10">
        <div className="grid md:grid-cols-2 gap-8">
          {labs.map((lab, index) => (
            <Card key={index} className="border-2 border-transparent hover:border-blue-200 overflow-hidden group hover-lift">
              <div className={`h-3 bg-gradient-to-r ${lab.color}`}></div>
              <CardContent className="p-8 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full filter blur-2xl opacity-30"></div>
                
                <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${lab.color} rounded-2xl text-white mb-6 shadow-xl transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative z-10`}>
                  {lab.icon}
                </div>
                
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 relative z-10">
                  {lab.institution}
                </h3>
                
                <p className="text-sm text-slate-500 mb-4 font-semibold relative z-10">{lab.period}</p>
                
                <p className="text-lg text-slate-700 mb-6 leading-relaxed relative z-10">
                  {lab.description}
                </p>
                
                <div className="relative z-10">
                  <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Wrench size={20} className="text-blue-600" />
                    Facilities & Equipment
                  </h4>
                  <ul className="space-y-3">
                    {lab.facilities.map((facility, idx) => (
                      <li key={idx} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                        <Beaker size={18} className="text-cyan-600 mt-1 flex-shrink-0" />
                        <span className="text-slate-700">{facility}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 shadow-xl border-2 border-blue-100">
          <div className="text-center">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              Impact & Achievement
            </h3>
            <p className="text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Established comprehensive laboratories for composite materials research, enabling cutting-edge experimental work 
              in manufacturing, testing, and characterization of fiber-reinforced composites. These facilities have supported 
              numerous research projects, student theses, and industry collaborations.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Labs;
